import { ChevronDown, CircleHelp, Code2, House, Languages } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import { businessQrUrl } from './assets/business-qr'
import { isInternational } from './config'

const docsUrl = 'https://ccnphfhqs21z.feishu.cn/wiki/F5krwD16viZoF0kKkvDcrZNYnhb'
const githubUrl = 'https://github.com/78/xiaozhi-esp32'
const filingUrl = 'https://beian.miit.gov.cn/'
const algorithmRecordUrl = 'https://www.cac.gov.cn/2025-05/19/c_1749365589879703.htm'
const businessEmail = 'chenlan@tenclass.com'

const locales = ['en', 'zh-CN', 'zh-TW', 'ja', 'vi'] as const
type Locale = (typeof locales)[number]

type Copy = {
  languageName: string
  brand: string
  pageTitle: string
  pageDescription: string
  welcome: string
  tutorial: string
  console: string
  copyright: string
  algorithmRecord: string
  aiDisclaimer: string
  business: string
  businessQrAlt: string
  notFoundTitle: string
  notFoundDescription: string
  backHome: string
}

const defaultPageTitle = 'XiaoZhi AI | Connect AI Agents to Real-World Open-Source Hardware'
const defaultPageDescription =
  'XiaoZhi AI connects intelligent agents to real-world open-source hardware. Manage ESP32 voice devices, AI agents, and developer tools in one console.'

const copy: Record<Locale, Copy> = {
  en: {
    languageName: 'English',
    brand: 'XiaoZhi AI',
    pageTitle: defaultPageTitle,
    pageDescription: defaultPageDescription,
    welcome: "Let's explore the fascinating world of AI and robotics together!",
    tutorial: 'DIY Tutorial',
    console: 'Console',
    copyright: 'XiaoZhi AI Control Panel 2.0',
    algorithmRecord: 'Algorithm Record No.440305324629801250025',
    aiDisclaimer: 'This service is powered by AI. AI-generated conversations are for reference only.',
    business: 'Business cooperation',
    businessQrAlt: 'Business cooperation QR code',
    notFoundTitle: 'Page not found',
    notFoundDescription: 'The page you requested does not exist or has been moved.',
    backHome: 'Back to home',
  },
  'zh-CN': {
    languageName: '简体中文',
    brand: '小智 AI',
    pageTitle: '小智 AI｜让智能体连接真实世界的开源硬件',
    pageDescription: '小智 AI，让智能体连接真实世界的开源硬件。管理 ESP32 语音设备、智能体与开发者能力。',
    welcome: '让我们一起探索人工智能与机器人技术的迷人世界！',
    tutorial: 'DIY 教程',
    console: '控制台',
    copyright: '小智 AI 控制面板 2.0',
    algorithmRecord: '网信算备440305324629801250025号',
    aiDisclaimer: '本服务基于人工智能技术提供，对话内容由 AI 生成，仅供参考',
    business: '扫码商务合作',
    businessQrAlt: '商务合作二维码',
    notFoundTitle: '页面未找到',
    notFoundDescription: '你访问的页面不存在或已被移动。',
    backHome: '返回首页',
  },
  'zh-TW': {
    languageName: '繁體中文',
    brand: '小智 AI',
    pageTitle: '小智 AI｜讓智慧體連接真實世界的開源硬體',
    pageDescription: '小智 AI，讓智慧體連接真實世界的開源硬體。管理 ESP32 語音裝置、智慧體與開發者能力。',
    welcome: '讓我們一起探索人工智慧與機器人技術的迷人世界！',
    tutorial: 'DIY 教程',
    console: '控制台',
    copyright: '小智 AI 控制面板 2.0',
    algorithmRecord: '網信算備440305324629801250025號',
    aiDisclaimer: '本服務基於人工智慧技術提供，對話內容由 AI 生成，僅供參考',
    business: '掃碼商務合作',
    businessQrAlt: '商務合作二維碼',
    notFoundTitle: '找不到頁面',
    notFoundDescription: '你造訪的頁面不存在或已被移動。',
    backHome: '返回首頁',
  },
  ja: {
    languageName: '日本語',
    brand: 'XiaoZhi AI',
    pageTitle: 'XiaoZhi AI｜現実世界のオープンソースハードウェアにつながる AI エージェント',
    pageDescription:
      'XiaoZhi AI はインテリジェントエージェントを現実世界のオープンソースハードウェアにつなぎます。ESP32 音声デバイス、AI エージェント、開発者ツールを一つのコンソールで管理できます。',
    welcome: '一緒に AI とロボット工学の魅力的な世界を探検しましょう！',
    tutorial: 'DIY チュートリアル',
    console: 'コンソール',
    copyright: 'XiaoZhi AI コントロールパネル 2.0',
    algorithmRecord: '网信算备440305324629801250025号',
    aiDisclaimer: '本サービスは人工知能技術に基づいて提供され、AI が生成した会話は参考情報です。',
    business: 'ビジネス提携',
    businessQrAlt: 'ビジネス提携 QR コード',
    notFoundTitle: 'ページが見つかりません',
    notFoundDescription: 'お探しのページは存在しないか、移動した可能性があります。',
    backHome: 'ホームに戻る',
  },
  vi: {
    languageName: 'Tiếng Việt',
    brand: 'XiaoZhi AI',
    pageTitle: 'XiaoZhi AI | Kết nối tác nhân AI với phần cứng mã nguồn mở thực tế',
    pageDescription:
      'XiaoZhi AI kết nối tác nhân thông minh với phần cứng mã nguồn mở trong thế giới thực. Quản lý thiết bị giọng nói ESP32, tác nhân AI và công cụ phát triển trong một bảng điều khiển.',
    welcome: 'Hãy cùng khám phá thế giới thú vị của AI và robot!',
    tutorial: 'Hướng dẫn DIY',
    console: 'Bảng điều khiển',
    copyright: 'XiaoZhi AI Control Panel 2.0',
    algorithmRecord: 'Algorithm Record No.440305324629801250025',
    aiDisclaimer: 'Dịch vụ này sử dụng AI. Nội dung hội thoại do AI tạo chỉ mang tính tham khảo.',
    business: 'Hợp tác kinh doanh',
    businessQrAlt: 'Mã QR hợp tác kinh doanh',
    notFoundTitle: 'Không tìm thấy trang',
    notFoundDescription: 'Trang bạn yêu cầu không tồn tại hoặc đã được di chuyển.',
    backHome: 'Về trang chủ',
  },
}

function supportsHoverOpen() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches
}

function setMetaContent(selector: string, content: string) {
  const element = document.head.querySelector(selector)
  if (element instanceof HTMLMetaElement) element.content = content
}

function detectLocale(): Locale {
  try {
    const storedLocale = localStorage.getItem('locale')
    if (storedLocale && locales.includes(storedLocale as Locale)) return storedLocale as Locale
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }

  const browserLocale = navigator.language.toLowerCase()
  if (browserLocale.startsWith('zh-tw') || browserLocale.startsWith('zh-hk') || browserLocale.startsWith('zh-mo')) return 'zh-TW'
  if (browserLocale.startsWith('zh')) return 'zh-CN'
  if (browserLocale.startsWith('ja')) return 'ja'
  if (browserLocale.startsWith('vi')) return 'vi'
  return 'en'
}

function GitHubMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 0C3.58 0 0 3.64 0 8.13c0 3.59 2.29 6.63 5.47 7.71.4.08.55-.18.55-.39 0-.19-.01-.83-.01-1.51-2.01.38-2.53-.5-2.69-.96-.09-.23-.48-.96-.82-1.15-.28-.15-.68-.53-.01-.54.63-.01 1.08.59 1.23.83.72 1.23 1.87.88 2.33.67.07-.53.28-.88.51-1.08-1.78-.21-3.64-.91-3.64-4.02 0-.89.31-1.62.82-2.19-.08-.21-.36-1.04.08-2.16 0 0 .67-.22 2.2.84A7.5 7.5 0 0 1 8 3.91a7.5 7.5 0 0 1 2 .27c1.53-1.06 2.2-.84 2.2-.84.44 1.12.16 1.95.08 2.16.51.57.82 1.3.82 2.19 0 3.12-1.87 3.81-3.65 4.02.29.25.54.74.54 1.5 0 1.08-.01 1.95-.01 2.22 0 .22.15.47.55.39A8.16 8.16 0 0 0 16 8.13C16 3.64 12.42 0 8 0Z" />
    </svg>
  )
}

export function App() {
  const [locale, setLocale] = useState<Locale>(detectLocale)
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false)
  const languageMenuId = useId()
  const languageDropdownRef = useRef<HTMLDivElement>(null)
  const languageCloseTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null)
  const pointerTypeRef = useRef('')
  const t = copy[locale]
  const isNotFound = window.location.pathname !== '/'
  const pageTitle = isNotFound ? `404 | ${t.brand}` : t.pageTitle
  const pageDescription = isNotFound ? t.notFoundDescription : t.pageDescription

  useEffect(() => {
    document.documentElement.lang = locale
    document.title = pageTitle
    setMetaContent('meta[name="description"]', pageDescription)
    setMetaContent('meta[property="og:title"]', pageTitle)
    setMetaContent('meta[property="og:description"]', pageDescription)
    setMetaContent('meta[property="og:locale"]', locale === 'en' ? 'en_US' : locale.replace('-', '_'))
    setMetaContent('meta[name="twitter:title"]', pageTitle)
    setMetaContent('meta[name="twitter:description"]', pageDescription)
    try {
      localStorage.setItem('locale', locale)
    } catch {
      // The selected locale still applies for this page session.
    }
  }, [locale, pageDescription, pageTitle])

  useEffect(() => {
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!languageDropdownRef.current?.contains(event.target as Node)) setLanguageMenuOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLanguageMenuOpen(false)
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      document.removeEventListener('keydown', closeOnEscape)
      if (languageCloseTimerRef.current !== null) window.clearTimeout(languageCloseTimerRef.current)
    }
  }, [])

  const clearLanguageCloseTimer = () => {
    if (languageCloseTimerRef.current !== null) window.clearTimeout(languageCloseTimerRef.current)
    languageCloseTimerRef.current = null
  }

  const openLanguageMenu = () => {
    clearLanguageCloseTimer()
    setLanguageMenuOpen(true)
  }

  const scheduleLanguageMenuClose = () => {
    clearLanguageCloseTimer()
    languageCloseTimerRef.current = window.setTimeout(() => {
      setLanguageMenuOpen(false)
      languageCloseTimerRef.current = null
    }, 180)
  }

  const selectLocale = (nextLocale: Locale, button: HTMLButtonElement) => {
    clearLanguageCloseTimer()
    setLocale(nextLocale)
    setLanguageMenuOpen(false)
    button.blur()
  }

  return (
    <div className="legacy-home">
      <header className="legacy-header">
        <a className="legacy-brand" href="/">{t.brand}</a>
        <div
          ref={languageDropdownRef}
          className={`language-dropdown${languageMenuOpen ? ' is-open' : ''}`}
          onPointerDown={(event) => {
            pointerTypeRef.current = event.pointerType
          }}
          onPointerEnter={(event) => {
            // Phones fire pointerleave when the finger lifts, which would close the menu in the same tap.
            if (event.pointerType !== 'mouse' || !supportsHoverOpen()) return
            openLanguageMenu()
          }}
          onPointerLeave={(event) => {
            if (event.pointerType !== 'mouse' || !supportsHoverOpen()) return
            scheduleLanguageMenuClose()
          }}
          onFocusCapture={(event) => {
            if (pointerTypeRef.current === 'touch' || pointerTypeRef.current === 'pen') return
            if (event.target instanceof HTMLElement && event.target.matches(':focus-visible')) openLanguageMenu()
          }}
          onBlurCapture={(event) => {
            const nextTarget = event.relatedTarget
            if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return
            // Touch taps blur the trigger in the same gesture. Outside taps close via pointerdown.
            if (pointerTypeRef.current === 'touch' || pointerTypeRef.current === 'pen') return
            clearLanguageCloseTimer()
            setLanguageMenuOpen(false)
          }}
        >
          <button
            className="language-trigger"
            type="button"
            aria-label="Language"
            aria-haspopup="menu"
            aria-controls={languageMenuId}
            aria-expanded={languageMenuOpen}
            onClick={(event) => {
              clearLanguageCloseTimer()
              // Touch taps toggle. Mouse and keyboard only open; hover, Escape, or an outside tap closes.
              if (event.detail > 0 && (pointerTypeRef.current === 'touch' || pointerTypeRef.current === 'pen' || !supportsHoverOpen())) {
                setLanguageMenuOpen((open) => !open)
                return
              }
              setLanguageMenuOpen(true)
            }}
          >
            <Languages size={16} aria-hidden="true" />
            <span>{t.languageName}</span>
            <ChevronDown size={13} aria-hidden="true" />
          </button>
          <div id={languageMenuId} className="language-menu" role="menu" aria-label="Language">
            {locales.map((item) => (
              <button
                key={item}
                className={item === locale ? 'is-selected' : undefined}
                type="button"
                role="menuitemradio"
                aria-checked={item === locale}
                onClick={(event) => selectLocale(item, event.currentTarget)}
              >
                {copy[item].languageName}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="legacy-content">
        {isNotFound ? (
          <section className="not-found-panel">
            <div className="not-found-code" aria-hidden="true">404</div>
            <h1>{t.notFoundTitle}</h1>
            <p>{t.notFoundDescription}</p>
            <div className="legacy-actions">
              <a className="legacy-button" href="/">
                <House size={16} aria-hidden="true" />{t.backHome}
              </a>
              <a className="legacy-button legacy-button-primary" href="/console/">
                <Code2 size={16} aria-hidden="true" />{t.console}
              </a>
            </div>
          </section>
        ) : (
          <section className="welcome-panel">
            <svg className="legacy-robot" width="120" height="120" viewBox="0 0 120 120" role="img" aria-label="XiaoZhi robot">
              <rect x="30" y="20" width="60" height="50" rx="5" fill="#e0e0e0" />
              <circle cx="45" cy="40" r="8" fill="#000" />
              <circle cx="75" cy="40" r="8" fill="#000" />
              <line x1="60" y1="20" x2="60" y2="5" stroke="#1677ff" strokeWidth="3" />
              <circle cx="60" cy="5" r="3" fill="#1677ff" />
              <path d="M 40 55 Q 60 65 80 55" stroke="#666" strokeWidth="3" fill="none" />
              <rect x="40" y="70" width="40" height="30" rx="3" fill="#e0e0e0" />
              <rect x="15" y="75" width="25" height="8" rx="4" fill="#e0e0e0" />
              <rect x="80" y="75" width="25" height="8" rx="4" fill="#e0e0e0" />
            </svg>
            <p>{t.welcome}</p>
            <div className="legacy-actions">
              <a className="legacy-button" href={docsUrl} target="_blank" rel="noreferrer">
                <CircleHelp size={16} aria-hidden="true" />{t.tutorial}
              </a>
              <a className="legacy-button" href={githubUrl} target="_blank" rel="noreferrer">
                <GitHubMark />GitHub
              </a>
              <a className="legacy-button legacy-button-primary" href="/console/">
                <Code2 size={16} aria-hidden="true" />{t.console}
              </a>
            </div>
          </section>
        )}
      </main>

      <footer className="legacy-footer">
        <div className="footer-inner">
          <div className="footer-copy">
            <div>© {new Date().getFullYear()} {t.copyright}</div>
            {!isInternational ? (
              <div className="footer-records">
                <a href={filingUrl} target="_blank" rel="noreferrer">粤ICP备2022121736号-2</a>
                <a href={algorithmRecordUrl} target="_blank" rel="noreferrer">{t.algorithmRecord}</a>
              </div>
            ) : null}
            <div className="ai-disclaimer">{t.aiDisclaimer}</div>
          </div>
          <div className="business-contact">
            <img src={businessQrUrl} alt={t.businessQrAlt} />
            <div>
              <div className="business-label">{t.business}</div>
              <a href={`mailto:${businessEmail}`}>{businessEmail}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
